/* ============================================================
   SAM-657 — the service badge (the icon the app paints over the service photo).

   Stored in `service.settings.badge` as one of a fixed set of keys; the app maps the key to its icon
   and label. The backend (sam-lambda-functions PR #468, `service_badge.py`) rejects any other key
   with 400 `error_code: "invalid_service_badge"`. With no key stored, the badge SUGGESTED by the
   service's setup (`service.subtype`) is used — `SUGGESTED_BADGE_BY_SUBTYPE` mirrors the backend
   table and `GET /services` returns the resolved value as `badge` / `badge_source`.
   ============================================================ */
import type { LucideIcon } from 'lucide-react';
import { DoorOpen, Flag, Sun, Tent, Users, Utensils } from 'lucide-react';

export type ServiceBadgeKey = 'cabana' | 'day_pass' | 'green_fee' | 'class' | 'table' | 'admission';

export const SERVICE_BADGE_OPTIONS: Array<{ key: ServiceBadgeKey; label: string; icon: LucideIcon }> = [
  { key: 'cabana', label: 'Cabana', icon: Tent },
  { key: 'day_pass', label: 'Day pass', icon: Sun },
  { key: 'green_fee', label: 'Green fee', icon: Flag },
  { key: 'class', label: 'Class', icon: Users },
  { key: 'table', label: 'Table', icon: Utensils },
  { key: 'admission', label: 'Admission', icon: DoorOpen },
];

export const DEFAULT_BADGE: ServiceBadgeKey = 'admission';

/** Mirror of `SUGGESTED_BADGE_BY_SUBTYPE` in functions/booking/service_badge.py (the setup -> badge table). */
const SUGGESTED_BADGE_BY_SUBTYPE: Record<string, ServiceBadgeKey> = {
  cabana: 'cabana',
  daypass: 'day_pass',
  minconsumo: 'day_pass',
  teetime: 'green_fee',
  class: 'class',
  private: 'class',
  tables: 'table',
  single: 'admission',
  multiday: 'admission',
  stay: 'admission',
  equipment: 'admission',
};

export function isServiceBadge(value: unknown): value is ServiceBadgeKey {
  return typeof value === 'string' && SERVICE_BADGE_OPTIONS.some((o) => o.key === value);
}

export function suggestedBadge(subtype: string | null | undefined): ServiceBadgeKey {
  return SUGGESTED_BADGE_BY_SUBTYPE[(subtype || '').trim().toLowerCase()] ?? DEFAULT_BADGE;
}

export function badgeOption(key: ServiceBadgeKey) {
  return SERVICE_BADGE_OPTIONS.find((o) => o.key === key) ?? SERVICE_BADGE_OPTIONS[SERVICE_BADGE_OPTIONS.length - 1];
}

/** The `settings.badge` to save: the chosen key, or nothing when the admin left it on "Suggested". */
export function badgeForSave(value: ServiceBadgeKey | ''): { badge: ServiceBadgeKey } | Record<string, never> {
  return isServiceBadge(value) ? { badge: value } : {};
}

/** The server's rejection of an unknown badge: 400 `{ error_code: 'invalid_service_badge', details: { allowed } }`. */
export function badgeSaveIssue(error: unknown): string | null {
  const data = (error as { response?: { data?: any } } | null)?.response?.data;
  if ((data?.error_code ?? data?.code) !== 'invalid_service_badge') return null;
  const allowed: unknown = data?.details?.allowed;
  const list = Array.isArray(allowed) ? allowed.join(', ') : SERVICE_BADGE_OPTIONS.map((o) => o.key).join(', ');
  return `The server does not accept that badge. Allowed: ${list}.`;
}
